import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Building2, LogOut, LayoutDashboard, Inbox, FileText, UserCircle, Menu, X, BarChart3, MessageSquare, CreditCard, Send } from "lucide-react";

const NAV = [
  { to: "/vendor", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/vendor/dispatches", label: "Dispatches", icon: Send, id: "dispatches" },
  { to: "/vendor/orders", label: "Orders", icon: Inbox, id: "orders" },
  { to: "/vendor/services", label: "Services", icon: FileText, id: "services" },
  { to: "/vendor/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/vendor/invoices", label: "Invoices", icon: FileText, id: "invoices" },
  { to: "/vendor/messages", label: "Messages", icon: MessageSquare, id: "messages" },
  { to: "/vendor/payments", label: "Payments (Stripe)", icon: CreditCard, id: "payments" },
  { to: "/vendor/profile", label: "Profile", icon: UserCircle, id: "profile" },
];

export default function VendorShell() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [loc.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside
        data-testid="vendor-shell-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-slate-200 transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform flex flex-col`}
      >
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400">Vendor portal</div>
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="vendor-shell-vendor-name">
                {user?.vendor_name || "Vendor"}
              </div>
            </div>
          </div>
        </div>
        <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end, id }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`vendor-shell-nav-${id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition ${
                  isActive ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <Icon className="w-4 h-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100 bg-white">
          <button
            onClick={() => {
              logout();
              nav("/login");
            }}
            data-testid="vendor-shell-logout"
            className="w-full inline-flex items-center justify-center gap-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 lg:px-8 py-3 flex items-center gap-3">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-slate-100"
            onClick={() => setMobileOpen((m) => !m)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="text-xs font-semibold text-slate-500">Vendor</div>
          <span className="text-slate-300">·</span>
          <div className="text-sm font-semibold text-slate-900 truncate">{user?.vendor_name}</div>
          <div className="ml-auto text-xs text-slate-500 hidden sm:block">{user?.email}</div>
        </header>
        <div className="px-4 lg:px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
